import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, startGeneration, getStatus, previewUrl, downloadUrl, githubPush, PROVIDER_META, STACK_META } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ArrowLeft, Sparkles, Send, Code2, Eye, Copy, Download, RefreshCw, Save, Loader2, User, Bot,
  File, FileCode, Database, FileJson, Github, ExternalLink,
} from "lucide-react";

function fileIcon(path) {
  if (path.endsWith(".sql")) return Database;
  if (path.endsWith(".json")) return FileJson;
  if (path.endsWith(".php") || path.endsWith(".js") || path.endsWith(".html") || path.endsWith(".css")) return FileCode;
  return File;
}

export default function Builder() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [activePath, setActivePath] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("preview");
  const [previewKey, setPreviewKey] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [ghOpen, setGhOpen] = useState(false);
  const [ghPat, setGhPat] = useState("");
  const [ghRepo, setGhRepo] = useState("");
  const [ghPrivate, setGhPrivate] = useState(true);
  const [ghMsg, setGhMsg] = useState("Publish from FORGE.AI");
  const [pushing, setPushing] = useState(false);
  const [ghResult, setGhResult] = useState(null);
  const chatEndRef = useRef(null);
  const pollRef = useRef(null);

  const refreshData = async () => {
    const [pr, msg] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/messages`),
    ]);
    setProject(pr.data);
    const f = pr.data.files || [];
    setFiles(f);
    setDirty(false);
    setActivePath((prev) => (prev && f.some((x) => x.path === prev) ? prev : (f[0]?.path || null)));
    setMessages(msg.data);
    return pr.data;
  };

  const load = async () => {
    try {
      const data = await refreshData();
      if (data.generating) { setBusy(true); startPolling(); }
    } catch (e) {
      toast.error("Layihə yüklənmədi");
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await getStatus(id);
        if (!s.generating) {
          stopPolling();
          await refreshData();
          setPreviewKey((k) => k + 1);
          setTab("preview");
          setBusy(false);
          toast.success("Hazırdır ✓");
        }
      } catch (e) {
        stopPolling();
        setBusy(false);
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    load();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    if (!prompt.trim() || busy) return;
    const p = prompt.trim();
    setPrompt("");
    setMessages((m) => [...m, { id: Date.now() + "u", role: "user", content: p }]);
    setBusy(true);
    try {
      await startGeneration(id, p);
      startPolling();
    } catch (e) {
      setBusy(false);
      const detail = e?.response?.data?.detail || "Başlanmadı";
      toast.error("Xəta: " + detail);
      setMessages((m) => [...m, { id: Date.now() + "e", role: "assistant", content: "⚠️ " + detail }]);
    }
  };

  const activeFile = files.find((f) => f.path === activePath);

  const updateActiveContent = (val) => {
    setFiles((fs) => fs.map((f) => (f.path === activePath ? { ...f, content: val } : f)));
    setDirty(true);
  };

  const saveFiles = async () => {
    try {
      await api.put(`/projects/${id}/files`, { files });
      setDirty(false);
      setPreviewKey((k) => k + 1);
      toast.success("Yadda saxlanıldı");
    } catch (e) {
      toast.error("Saxlanılmadı");
    }
  };

  const copyFile = () => {
    if (!activeFile) return;
    navigator.clipboard.writeText(activeFile.content);
    toast.success("Kopyalandı");
  };

  const downloadFile = () => {
    if (!activeFile) return;
    const blob = new Blob([activeFile.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = activeFile.path.split("/").pop(); a.click();
    URL.revokeObjectURL(url);
  };

  const downloadZip = () => {
    const a = document.createElement("a");
    a.href = downloadUrl(id);
    a.click();
  };

  const openGithub = () => {
    setGhResult(null);
    if (!ghRepo) {
      const slug = (project?.name || "forge-project").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "forge-project";
      setGhRepo(slug);
    }
    setGhOpen(true);
  };

  const doPush = async () => {
    if (!ghPat.trim() || !ghRepo.trim()) { toast.error("Token və repo adı lazımdır"); return; }
    setPushing(true);
    setGhResult(null);
    try {
      const res = await githubPush(id, { pat: ghPat.trim(), repo: ghRepo.trim(), private: ghPrivate, commit_message: ghMsg || "Publish from FORGE.AI" });
      setGhResult(res);
      setGhPat("");
      toast.success("GitHub-a göndərildi ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Göndərilmədi");
    } finally {
      setPushing(false);
    }
  };

  const meta = project ? PROVIDER_META[project.provider] : null;
  const stackMeta = project ? STACK_META[project.stack] : null;
  const hasCode = files.length > 0;

  return (
    <div className="h-screen w-screen bg-black text-white flex flex-col overflow-hidden">
      <header className="h-14 border-b border-[#222] flex items-center justify-between px-4 shrink-0 bg-[#0a0a0a]">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-cyan-500 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-black" />
            </div>
            <span className="font-medium text-sm" data-testid="project-title">{project?.name || "..."}</span>
          </div>
        </div>
        {meta && (
          <div className="flex items-center gap-2">
            {hasCode && (
              <>
                <Button onClick={downloadZip} variant="ghost" size="sm" className="rounded-sm h-8 text-xs text-zinc-300 hover:text-white border border-[#333]" data-testid="download-zip-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> ZIP
                </Button>
                <Button onClick={openGithub} variant="ghost" size="sm" className="rounded-sm h-8 text-xs text-black bg-cyan-500 hover:bg-cyan-400" data-testid="github-push-btn">
                  <Github className="w-3.5 h-3.5 mr-1.5" /> GitHub
                </Button>
              </>
            )}
            {stackMeta && (
              <span className="font-mono text-[9px] uppercase tracking-widest border border-[#333] px-2 py-1 rounded-sm text-zinc-300" data-testid="stack-badge">
                {stackMeta.backend ? <Database className="w-3 h-3 inline mr-1 text-cyan-500" /> : null}{stackMeta.label}
              </span>
            )}
            <div className="hidden lg:flex items-center gap-2 border border-[#333] px-3 py-1 rounded-sm">
              <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
              <span className="text-xs font-medium">{meta.label}</span>
              <span className="font-mono text-[10px] text-zinc-500">{project.model}</span>
            </div>
          </div>
        )}
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
        {/* Chat panel */}
        <div className="lg:col-span-4 border-r border-[#222] flex flex-col overflow-hidden bg-[#0a0a0a]">
          <div className="px-4 py-3 border-b border-[#222] shrink-0">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">AI Söhbət</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && !busy && (
              <div className="text-center pt-10">
                <Bot className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                <p className="text-sm text-zinc-500">Nə qurmaq istədiyini yaz.</p>
                <p className="text-xs text-zinc-600 mt-1">
                  {stackMeta?.backend ? 'məs. "İstifadəçi qeydiyyatı olan bloq, MySQL bazası ilə"' : 'məs. "Restoran üçün menyu saytı"'}
                </p>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-6 h-6 shrink-0 flex items-center justify-center rounded-sm ${m.role === "user" ? "bg-white text-black" : "bg-cyan-500 text-black"}`}>
                  {m.role === "user" ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                </div>
                <div className={`text-sm px-3 py-2 rounded-sm max-w-[85%] ${m.role === "user" ? "bg-[#1a1a1a] text-white" : "bg-[#111] text-zinc-300 border border-[#222]"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-3" data-testid="building-indicator">
                <div className="w-6 h-6 shrink-0 flex items-center justify-center rounded-sm bg-cyan-500 text-black">
                  <Bot className="w-3.5 h-3.5" />
                </div>
                <div className="text-sm px-3 py-2 rounded-sm bg-[#111] text-zinc-400 border border-[#222] flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-cyan-500" />
                  AI layihəni qurur... <span className="font-mono text-xs text-zinc-600">(bir neçə saniyə)</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="p-3 border-t border-[#222] shrink-0">
            <div className="relative">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Nə istəyirsən yaz..."
                disabled={busy}
                rows={3}
                className="rounded-sm bg-transparent border-[#333] focus-visible:ring-1 focus-visible:ring-cyan-500 resize-none pr-12 text-sm"
                data-testid="prompt-input"
              />
              <Button
                onClick={send} disabled={busy || !prompt.trim()} size="icon"
                className="absolute bottom-2 right-2 h-8 w-8 rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors"
                data-testid="send-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Code + Preview panel */}
        <div className="lg:col-span-8 flex flex-col overflow-hidden">
          <Tabs value={tab} onValueChange={setTab} className="flex flex-col h-full">
            <div className="h-11 border-b border-[#222] flex items-center justify-between px-3 shrink-0 bg-[#0a0a0a]">
              <TabsList className="bg-[#151515] rounded-sm h-8 p-0.5">
                <TabsTrigger value="preview" className="rounded-sm text-xs data-[state=active]:bg-cyan-500 data-[state=active]:text-black h-7" data-testid="tab-preview">
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
                </TabsTrigger>
                <TabsTrigger value="code" className="rounded-sm text-xs data-[state=active]:bg-cyan-500 data-[state=active]:text-black h-7" data-testid="tab-code">
                  <Code2 className="w-3.5 h-3.5 mr-1.5" /> Kod {hasCode ? `(${files.length})` : ""}
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-1.5">
                {tab === "preview" && hasCode && (
                  <Button onClick={() => setPreviewKey((k) => k + 1)} variant="ghost" size="sm" className="rounded-sm h-7 text-xs text-zinc-400 hover:text-white" data-testid="refresh-preview">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Yenilə
                  </Button>
                )}
                {tab === "code" && activeFile && (
                  <>
                    {dirty && (
                      <Button onClick={saveFiles} size="sm" className="rounded-sm h-7 text-xs bg-cyan-500 text-black hover:bg-cyan-400" data-testid="save-code">
                        <Save className="w-3.5 h-3.5 mr-1" /> Saxla
                      </Button>
                    )}
                    <Button onClick={copyFile} variant="ghost" size="sm" className="rounded-sm h-7 text-xs text-zinc-400 hover:text-white" data-testid="copy-code">
                      <Copy className="w-3.5 h-3.5 mr-1" /> Kopyala
                    </Button>
                    <Button onClick={downloadFile} variant="ghost" size="sm" className="rounded-sm h-7 text-xs text-zinc-400 hover:text-white" data-testid="download-code">
                      <Download className="w-3.5 h-3.5 mr-1" /> Yüklə
                    </Button>
                  </>
                )}
              </div>
            </div>

            <TabsContent value="preview" className="flex-1 m-0 overflow-hidden bg-white">
              {hasCode ? (
                <iframe
                  key={previewKey}
                  title="preview"
                  src={previewUrl(id, previewKey)}
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
                  data-testid="preview-iframe"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-black">
                  <div className="text-center">
                    <Eye className="w-10 h-10 text-zinc-800 mx-auto mb-3" />
                    <p className="text-zinc-600 text-sm">Preview burada görünəcək</p>
                    <p className="text-zinc-700 text-xs mt-1">Sol tərəfdə nə istədiyini yaz</p>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="code" className="flex-1 m-0 overflow-hidden">
              {hasCode ? (
                <div className="flex h-full">
                  {/* File explorer */}
                  <div className="w-52 shrink-0 border-r border-[#222] bg-[#0a0a0a] overflow-y-auto" data-testid="file-explorer">
                    <div className="px-3 py-2 border-b border-[#222]">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fayllar</span>
                    </div>
                    {files.map((f) => {
                      const Icon = fileIcon(f.path);
                      return (
                        <button
                          key={f.path}
                          onClick={() => setActivePath(f.path)}
                          data-testid={`file-${f.path}`}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-mono transition-colors ${activePath === f.path ? "bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-500" : "text-zinc-400 hover:bg-[#151515] border-l-2 border-transparent"}`}
                        >
                          <Icon className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{f.path}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Editor */}
                  <Textarea
                    value={activeFile?.content || ""}
                    onChange={(e) => updateActiveContent(e.target.value)}
                    placeholder="Fayl seç..."
                    className="w-full h-full rounded-none border-0 bg-[#0a0a0a] font-mono text-xs resize-none focus-visible:ring-0 p-4 text-zinc-300 leading-relaxed"
                    spellCheck={false}
                    data-testid="code-editor"
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a]">
                  <p className="text-zinc-600 text-sm">Hələ fayl yoxdur</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={ghOpen} onOpenChange={(o) => { setGhOpen(o); if (!o) { setGhPat(""); setGhResult(null); } }}>
        <DialogContent className="bg-[#0a0a0a] border-[#222] text-white rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight flex items-center gap-2">
              <Github className="w-5 h-5" /> GitHub-a göndər
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Layihə faylları bir commit ilə repoya push olunacaq. Token yalnız bu sorğu üçün istifadə olunur, saxlanılmır.
            </DialogDescription>
          </DialogHeader>
          {ghResult ? (
            <div className="py-4 space-y-4" data-testid="gh-result">
              <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 rounded-sm">
                <p className="text-sm text-emerald-400 mb-2">✓ Uğurla göndərildi (commit {ghResult.commit})</p>
                <a href={ghResult.url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 text-sm font-mono flex items-center gap-1 break-all" data-testid="gh-repo-link">
                  {ghResult.url} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                </a>
              </div>
              <Button onClick={() => setGhOpen(false)} className="w-full rounded-sm bg-cyan-500 text-black hover:bg-cyan-400">Bağla</Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-zinc-400">GitHub Personal Access Token</Label>
                <Input
                  type="password" value={ghPat} onChange={(e) => setGhPat(e.target.value)}
                  placeholder="github_pat_... və ya ghp_..."
                  className="rounded-sm bg-transparent border-[#333] focus:ring-1 focus:ring-cyan-500 font-mono text-sm"
                  data-testid="gh-pat-input"
                />
                <p className="text-[11px] text-zinc-600">
                  Token: GitHub → Settings → Developer settings → Personal access tokens.
                  İcazələr: <span className="text-zinc-400">Contents: Read/Write</span>, <span className="text-zinc-400">Administration: Read/Write</span> (yeni repo üçün).
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-zinc-400">Repo adı</Label>
                <Input
                  value={ghRepo} onChange={(e) => setGhRepo(e.target.value)}
                  placeholder="my-project"
                  className="rounded-sm bg-transparent border-[#333] focus:ring-1 focus:ring-cyan-500 font-mono text-sm"
                  data-testid="gh-repo-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-zinc-400">Commit mesajı</Label>
                <Input
                  value={ghMsg} onChange={(e) => setGhMsg(e.target.value)}
                  className="rounded-sm bg-transparent border-[#333] focus:ring-1 focus:ring-cyan-500 text-sm"
                  data-testid="gh-msg-input"
                />
              </div>
              <div className="flex items-center justify-between border border-[#333] p-3 rounded-sm">
                <div>
                  <p className="text-sm">Privat repo</p>
                  <p className="text-[11px] text-zinc-600">Repo mövcud deyilsə, avtomatik yaradılır</p>
                </div>
                <Switch checked={ghPrivate} onCheckedChange={setGhPrivate} data-testid="gh-private-switch" />
              </div>
              <DialogFooter>
                <Button onClick={doPush} disabled={pushing} className="w-full rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 font-medium" data-testid="gh-push-submit">
                  {pushing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Göndərilir...</> : <><Github className="w-4 h-4 mr-2" /> Push et</>}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
